import {
  SESSION_INVALIDATING,
  SESSION_SAFE_UPDATE,
} from './trainingTypeListSync';

export { SESSION_INVALIDATING, SESSION_SAFE_UPDATE };

export const SESSION_TOMBSTONE = Object.freeze({ __sessionTombstone: true });

export const IMPLICIT_UPDATE_AUDIT_FIELDS = Object.freeze([
  'updated_at',
  'updated_by_user',
  'updated_by_user_id',
]);

const MEMBERSHIP_FIELDS = new Set([
  'deactivation_date',
  'certificate_status',
  'custom_expiration',
  'state',
  'company_id',
  'company.name',
  'current_record',
  'custom_start_of_validity',
  'custom_end_of_validity',
]);

const FIELD_ALIASES = {
  name: ['name'],
  description: ['description'],
  record_description: ['record_description', 'current_record'],
  'company.name': ['company.name', 'company_id'],
  company_id: ['company_id', 'company.name'],
  'responsible.name': ['responsible.name', 'responsible_employee_id'],
  responsible_employee_id: ['responsible_employee_id', 'responsible.name'],
  protocol: ['protocol'],
  deactivation_date: ['deactivation_date', 'certificate_status'],
  certificate_status: ['certificate_status', 'deactivation_date'],
  custom_expiration: ['custom_expiration', 'state', 'custom_end_of_validity'],
  state: ['state', 'custom_expiration', 'custom_end_of_validity'],
  custom_start_of_validity: ['custom_start_of_validity', 'current_record'],
  custom_end_of_validity: ['custom_end_of_validity', 'custom_expiration', 'state', 'current_record'],
  current_record: ['current_record', 'custom_start_of_validity', 'custom_end_of_validity', 'state', 'record_description'],
  created_at: ['created_at'],
  created_by_user: ['created_at'],
  created_by_user_id: ['created_at'],
  updated_at: ['updated_at'],
  updated_by_user: ['updated_at'],
  updated_by_user_id: ['updated_at'],
};

function unwrapCertificateRow(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }
  return payload;
}

function scalar(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (value.id != null) return String(value.id);
    return '';
  }
  return String(value);
}

function currentRecordId(row) {
  const current = row?.current_record;
  if (current && typeof current === 'object' && current.id != null) return String(current.id);
  return '';
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    const aliases = FIELD_ALIASES[field] || [field];
    aliases.forEach((alias) => expanded.add(alias));
  });
  return expanded;
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function isDefaultIdAscSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'id' && dir === 'asc';
}

export function isSessionTombstone(value) {
  return value === SESSION_TOMBSTONE || value?.__sessionTombstone === true;
}

export function isTableCompleteCompanyCertRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  if (row.id == null || row.id === '') return false;
  return Object.prototype.hasOwnProperty.call(row, 'current_record') || Array.isArray(row._children);
}

export function collectCompanyCertChangedFields(previous, next, options = {}) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const nxt = next && typeof next === 'object' ? next : {};
  const changed = [];
  [
    'name',
    'description',
    'company_id',
    'deactivation_date',
    'certificate_status',
    'custom_expiration',
    'state',
    'custom_start_of_validity',
    'custom_end_of_validity',
    'record_description',
    'responsible_employee_id',
  ].forEach((key) => {
    if (scalar(prev[key]) !== scalar(nxt[key])) changed.push(key);
  });
  if (scalar(prev.company?.name) !== scalar(nxt.company?.name)) changed.push('company.name');
  if (scalar(prev.responsible?.name) !== scalar(nxt.responsible?.name)) changed.push('responsible.name');
  if (currentRecordId(prev) !== currentRecordId(nxt)) changed.push('current_record');
  if (options.implicitAudit) changed.push(...IMPLICIT_UPDATE_AUDIT_FIELDS);
  return Array.from(new Set(changed));
}

export function classifyCompanyCertListMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  if (detail.operation !== 'update') return SESSION_INVALIDATING;
  const row = unwrapCertificateRow(detail.row);
  if (!isTableCompleteCompanyCertRow(row)) return SESSION_INVALIDATING;
  const id = detail.id ?? row.id;
  if (id == null || id === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(id)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;

  const changedFields = Array.isArray(detail.changedFields) && detail.changedFields.length > 0
    ? Array.from(new Set([...detail.changedFields, ...IMPLICIT_UPDATE_AUDIT_FIELDS]))
    : collectCompanyCertChangedFields(detail.previous, row, { implicitAudit: true });

  const expanded = expandFields(changedFields);
  for (const field of expanded) {
    if (MEMBERSHIP_FIELDS.has(field)) return SESSION_INVALIDATING;
  }

  const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
  for (const filter of filters) {
    if (!filter?.field || !hasActiveValue(filter.value)) continue;
    const aliases = expandFields([filter.field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
  for (const sorter of sorters) {
    const field = sorter?.field || sorter?.column;
    if (!field) continue;
    if (isDefaultIdAscSorter(sorter) && !expanded.has('id')) continue;
    const aliases = expandFields([field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  return SESSION_SAFE_UPDATE;
}

export function applyCompanyCertPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.reduce((acc, row) => {
      if (!row || row.id == null) {
        acc.push(row);
        return acc;
      }
      const over = overlay.get(String(row.id));
      if (isSessionTombstone(over)) return acc;
      acc.push(over || row);
      return acc;
    }, []),
  };
}
